// src/pages/auth/Login.jsx
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import useAuthStore from '@/store/authStore'
import toast from 'react-hot-toast'
import { GraduationCap, Eye, EyeOff, Phone, Mail } from 'lucide-react'
import SocialLoginButtons from '@/components/SocialLoginButtons'
import LanguageSwitcher from '@/components/common/LanguageSwitcher'

export default function Login() {
  const { t } = useTranslation()
  const [mode, setMode] = useState('password') // 'password' | 'otp'
  const [otpSent, setOtpSent] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { login, sendOtp, verifyOtp, loading } = useAuthStore()
  const navigate = useNavigate()
  const { register, handleSubmit, watch, formState: { errors } } = useForm()

  const onPasswordLogin = async (data) => {
    const result = await login(data)
    if (result.success) {
      toast.success(t('auth.welcomeBack'))
      const role = result.data.user.role
      navigate(role === 'admin' ? '/admin/dashboard' :
               role === 'parent' ? '/parent/dashboard' : '/teacher/dashboard')
    } else {
      toast.error(result.error)
    }
  }

  const onSendOtp = async (data) => {
    const result = await sendOtp(data.phone)
    if (result.success) {
      setOtpSent(true)
      toast.success(t('auth.otpSent'))
    } else {
      toast.error(result.error || t('auth.failedOtp'))
    }
  }

  const onVerifyOtp = async (data) => {
    const result = await verifyOtp({ phone: data.phone, otp: data.otp })
    if (result.success) {
      toast.success(t('auth.loggedIn'))
      const role = result.data.user.role
      navigate(role === 'admin' ? '/admin/dashboard' :
               role === 'parent' ? '/parent/dashboard' : '/teacher/dashboard')
    } else {
      toast.error(result.error)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d2757] to-[#1a3a6b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Language switcher - top right */}
        <div className="flex justify-end mb-4">
          <div className="bg-white/10 rounded-lg">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-400 rounded-2xl mb-4 shadow-xl">
            <GraduationCap size={32} className="text-[#0d2757]" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            educ<span className="text-orange-400">za</span>
          </h1>
          <p className="text-white/60 mt-2 text-sm">{t('auth.platformTagline')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">{t('auth.signInToSchool')}</h2>

          {/* Mode toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => { setMode('password'); setOtpSent(false) }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
                mode === 'password' ? 'bg-white text-[#0d2757] shadow-sm' : 'text-gray-500'
              }`}
            >
              <Mail size={14} /> {t('auth.loginWithPassword')}
            </button>
            <button
              onClick={() => { setMode('otp'); setOtpSent(false) }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
                mode === 'otp' ? 'bg-white text-[#0d2757] shadow-sm' : 'text-gray-500'
              }`}
            >
              <Phone size={14} /> {t('auth.loginWithOtp')}
            </button>
          </div>

          {/* Password Login */}
          {mode === 'password' && (
            <form onSubmit={handleSubmit(onPasswordLogin)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.emailLabel')}</label>
                <input
                  {...register('email', { required: t('auth.emailRequired'), pattern: { value: /\S+@\S+\.\S+/, message: t('auth.invalidEmail') } })}
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757] focus:border-transparent"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.passwordLabel')}</label>
                <div className="relative">
                  <input
                    {...register('password', { required: t('auth.passwordRequired') })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757] focus:border-transparent"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-[#0d2757]">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-[#0d2757] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#1a3a6b] transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </form>
          )}

          {/* Social Login */}
          {mode === 'password' && <SocialLoginButtons />}

          {/* OTP Login */}
          {mode === 'otp' && (
            <form onSubmit={handleSubmit(otpSent ? onVerifyOtp : onSendOtp)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.phoneNumber')}</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-500 text-sm">+91</span>
                  <input
                    {...register('phone', { required: t('auth.phoneRequired'), pattern: { value: /^[6-9]\d{9}$/, message: t('auth.invalidPhone') } })}
                    type="tel" maxLength={10} placeholder="9876543210"
                    className="flex-1 border border-gray-300 rounded-r-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757] focus:border-transparent"
                    disabled={otpSent}
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
              </div>
              {otpSent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.enterOtp')}</label>
                  <input
                    {...register('otp', { required: t('auth.otpRequired'), minLength: { value: 6, message: t('auth.invalidOtp') } })}
                    type="text" maxLength={6} placeholder="123456"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-center text-xl tracking-widest font-bold focus:outline-none focus:ring-2 focus:ring-[#0d2757]"
                  />
                  {errors.otp && <p className="text-red-500 text-xs mt-1">{errors.otp.message}</p>}
                </div>
              )}
              <button type="submit" disabled={loading}
                className="w-full bg-[#0d2757] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#1a3a6b] transition-colors disabled:opacity-60">
                {loading ? t('auth.pleaseWait') : otpSent ? t('auth.verifyOtp') : t('auth.sendOtp')}
              </button>
              {otpSent && (
                <button type="button" onClick={() => setOtpSent(false)}
                  className="w-full text-sm text-gray-500 hover:text-gray-700">
                  {t('auth.changePhone')}
                </button>
              )}
            </form>
          )}

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.newSchool')}{' '}
            <Link to="/register" className="text-orange-500 font-semibold hover:underline">
              {t('auth.startFreeTrial')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
